import { API_BASE_URL } from "./advancedSettings";

export type RequestStatus = "pending" | "approved" | "denied";

export interface StreamRequestInfo {
  id: number;
  count: number;
  reason: string;
  status: RequestStatus;
  createdAt: string;
  decidedAt: string | null;
  note: string | null;
}

/** A request as an admin sees it, with the account behind it. */
export interface AdminStreamRequest extends StreamRequestInfo {
  uid: string;
  email: string;
  currentLimit: number;
  used: number;
}

export interface StreamQuota {
  limit: number;
  used: number;
  /** The most streams anyone can ask for. */
  max: number;
  pending: StreamRequestInfo | null;
  last: StreamRequestInfo | null;
  isAdmin: boolean;
}

export const MIN_REASON_LENGTH = 10;
export const MAX_REASON_LENGTH = 1000;

type Fetch = typeof fetch;
type Token = () => Promise<string>;

const call = async (getToken: Token, path: string, fetchFn: Fetch, init: RequestInit = {}) =>
  fetchFn(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { ...(init.body ? { "Content-Type": "application/json" } : {}), Authorization: `Bearer ${await getToken()}` },
  });

/** The message the API gave for a refusal, else a fallback. */
const messageOf = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return typeof body?.message === "string" ? body.message : fallback;
};

/** The account's stream limit, how many it has, and where its last request stands. Throws when the API does not answer. */
export async function fetchQuota(getToken: Token, fetchFn: Fetch = fetch): Promise<StreamQuota> {
  const response = await call(getToken, "/streamrequest/Mine", fetchFn);
  if (!response.ok) throw new Error(`Loading your stream limit failed (${response.status})`);
  return response.json();
}

export type SubmitResult = { ok: true; request: StreamRequestInfo } | { ok: false; message: string };

export async function submitStreamRequest(getToken: Token, count: number, reason: string, fetchFn: Fetch = fetch): Promise<SubmitResult> {
  try {
    const response = await call(getToken, "/streamrequest/Submit", fetchFn, { method: "POST", body: JSON.stringify({ count, reason }) });
    if (response.ok) return { ok: true, request: (await response.json()).request };
    return { ok: false, message: await messageOf(response, "We couldn't send your request. Please try again.") };
  } catch (error) {
    console.error("Could not send the stream request:", error);
    return { ok: false, message: "We couldn't send your request. Please try again." };
  }
}

/** Admin: requests waiting for a decision and the latest decided ones. Throws when the caller is not an admin or the API does not answer. */
export async function listStreamRequests(getToken: Token, fetchFn: Fetch = fetch): Promise<{ pending: AdminStreamRequest[]; decided: AdminStreamRequest[] }> {
  const response = await call(getToken, "/streamrequest/List", fetchFn);
  if (!response.ok) throw new Error(`Loading requests failed (${response.status})`);
  return response.json();
}

export async function decideStreamRequest(getToken: Token, id: number, approve: boolean, note: string, fetchFn: Fetch = fetch): Promise<{ ok: boolean; message?: string }> {
  try {
    const response = await call(getToken, "/streamrequest/Decide", fetchFn, { method: "POST", body: JSON.stringify({ id, approve, note }) });
    return response.ok ? { ok: true } : { ok: false, message: await messageOf(response, "The decision could not be saved.") };
  } catch (error) {
    console.error("Could not save the decision:", error);
    return { ok: false, message: "The decision could not be saved." };
  }
}

/** What is wrong with the request form, or null when it can be sent. */
export function requestFormProblem(count: number, reason: string, quota: Pick<StreamQuota, "limit" | "max">): string | null {
  if (!Number.isInteger(count) || count < 1 || count > quota.max) return `Choose a number from 1 to ${quota.max}.`;
  if (count === quota.limit) return "You can already have that many streams.";
  if (reason.trim().length < MIN_REASON_LENGTH) return `Please tell us why (at least ${MIN_REASON_LENGTH} characters).`;
  if (reason.trim().length > MAX_REASON_LENGTH) return `Please keep it under ${MAX_REASON_LENGTH} characters.`;
  return null;
}
